import React, { useCallback, useContext, useEffect, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import type { PingMessage, PongMessageType } from "../shared/types/pubsub";

import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { PubsubStatsCollectorContext } from "providers/PubsubStatsCollectorProvider";
import { store, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCourseVersion,
  selectCurrentCourseId,
  selectIsCourseVersionOutOfSync,
  selectShouldRequestAuth,
  selectUserIsInactive,
  selectWebsocketCourseId,
} from "store/selectors";
import {
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { selectStoredCollaborationServerVersion } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";
import { isLocalEnv } from "utils/env";
import { logInfoWithDebugFlag } from "utils/logger";
import { usePresentation } from "utils/presentation";
import { useParams } from "utils/routing";
import { useViewLevel } from "utils/urls";
import { getWindowId } from "utils/window";

// TODO: Merge these constants with backend in worksheet constants so they are shared
const TERMINATE_TIMEOUT_MS = 60 * 60000; // 60 minutes
const PROJECTOR_TERMINATE_TIMEOUT_MS = 4 * 60 * 60000; // 4 hours
const PING_INTERVAL_MS = 15000; // 15 seconds
const DEBOUNCE_TIME_MS = isLocalEnv ? 0 : 200; // For integration tests we need 0 but otherwise .2 seconds is good
const NORMAL_SOCKET_CLOSE_CODE = 1000; // websocket close code for normal closure
const SERVER_TIMEOUT_MS = 30000; // 30 seconds

interface PingComponentProps {
  socket: ReconnectingWebSocket;
}

// The ping component is responsible for sending pings to the server to keep the connection alive
// and to check if the server is still alive. If the server is not alive, it will attempt to reconnect
// the socket. We will also do an immediate ping if the page loses focus or the user's current page
// changes. If the user is inactive for 30 minutes, we will close the socket. To keep from sending
// too many events to the server, we swallow any events that happen within a specified debounce time.
export const PingComponent: React.FC<PingComponentProps> = ({ socket }) => {
  const lastPingTime = useRef<number>(0);
  const pingInterval = useRef<NodeJS.Timeout | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const serverLastResponseRef = useRef<number>(Date.now());
  const currentCourseIdRef = useRef<string | null>(null);
  const pathParams = useParams();
  const pageId = pathParams.pageId;

  const isCourseVersionOutOfSync = useAppSelector(selectIsCourseVersionOutOfSync);
  const shouldRequestAuth = useAppSelector(selectShouldRequestAuth);
  const userIsInactive = useAppSelector(selectUserIsInactive);
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isStableMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const isInVirtualClass = isObservingClass || isStableMainAvTab;
  const { isFollowing } = usePresentation();

  const isInVirtualClassRef = React.useRef(isInVirtualClass);

  const { monitoring } = useViewLevel();

  const dispatch = useAppDispatch();

  // Using ref here to keep the component from unmounting when these values change
  const pageIdRef = useRef<string | null>(pageId);
  const isFollowingRef = useRef<boolean>(isFollowing);

  // Check whether the server is still alive and reconnect if not
  // assuming we have a valid websocket connection
  const checkServer = useCallback(() => {
    if (
      serverLastResponseRef.current + SERVER_TIMEOUT_MS < Date.now() &&
      socket.readyState === WebSocket.OPEN &&
      !userIsInactive
    ) {
      logInfoWithDebugFlag(
        "debug_sharedb",
        `Server is not responding in ${SERVER_TIMEOUT_MS}ms, reconnecting...`,
      );
      socket.reconnect(); // Try to reconnect if the backend server is down
    }
  }, [socket, userIsInactive]);

  // Send a ping to the server if the socket is open and it's been long enough since the last ping
  const sendPing = useCallback(() => {
    const currentTime = Date.now();
    const state = store.getState();
    let websocketCourseId = selectWebsocketCourseId(state);
    // TODO: when we don't have a query we pass in "null" as a string in upgrading the websocket
    // we might want to remove the parameter entirely instead, though that could cause other issues.
    // For now, we'll leave it as is, but properly detect it here.
    if (websocketCourseId === "null") websocketCourseId = null;

    // Keep this reference just to ensure we don't send a duplicate auth update
    // when we switch between courses
    const currentCourseId = selectCurrentCourseId(state);

    if (
      socket.readyState === WebSocket.OPEN &&
      currentTime - lastPingTime.current >= DEBOUNCE_TIME_MS
    ) {
      const needsAuth =
        selectShouldRequestAuth(state) ||
        (currentCourseIdRef.current === currentCourseId &&
          (!!websocketCourseId || !!currentCourseId) &&
          websocketCourseId !== currentCourseId);

      const message: PingMessage = {
        a: "ping",
        pageId: pageIdRef.current,
        windowId: getWindowId(),
        active: document.hasFocus() ? "1" : "0",
        following: isFollowing ? "1" : "0",
        courseVersion: websocketCourseId === currentCourseId ? selectCourseVersion(state) : 0,
        startTime: Date.now(),
        needsAuth,
        saveActivity: !monitoring,
      };

      socket.send(JSON.stringify(message));
      lastPingTime.current = currentTime;
      currentCourseIdRef.current = currentCourseId;
      return true;
    }

    return false;
  }, [socket, monitoring, isFollowing]);

  // When we don't receive messages from the websocket in sequential order,
  // we know that the course version is out of sync, so we send a ping to
  // the server to receive all the messages from the current course version
  // up to the latest one.
  useEffect(() => {
    if (isCourseVersionOutOfSync) {
      sendPing();
      dispatch(viewStateActions.setIsCourseVersionOutOfSync(false));
    }
  }, [dispatch, sendPing, isCourseVersionOutOfSync]);

  // Stop pinging the server
  const stopPingInterval = useCallback(() => {
    clearInterval(pingInterval.current);
    pingInterval.current = null;
  }, []);

  // Stop waiting 30 minutes to close the socket
  const stopCloseTimeout = useCallback(() => {
    logInfoWithDebugFlag("debug_pubsub", "Stopping close socket timeout due to user activity");
    clearTimeout(closeTimeout.current);
    closeTimeout.current = null;
  }, []);

  // Start pinging the server every 30 seconds and checking if the server responsds
  const startPingInterval = useCallback(
    (inactive = userIsInactive) => {
      if (inactive) {
        return;
      }

      // If we started to close due to inactivity, stop closing
      // but only if the tab is active.  This can happen if
      // we immediate leave the tab after receiving focus
      if (document.hasFocus()) {
        stopCloseTimeout();
      }

      // Check that we didn't get disconnected
      if (socket.readyState !== WebSocket.OPEN && socket.readyState !== WebSocket.CONNECTING) {
        logInfoWithDebugFlag(
          "debug_sharedb",
          "Socket not open, reconnecting before starting ping interval",
        );
        socket.reconnect();
      }

      sendPing();

      // Only start the interval if we sent a ping or there isn't one already running
      if (!pingInterval.current) {
        // Start our server check timer
        serverLastResponseRef.current = Date.now();

        // Make sure we clear any existing one
        stopPingInterval();

        pingInterval.current = setInterval(() => {
          checkServer();
          sendPing();
        }, PING_INTERVAL_MS);
      }
    },
    [socket, sendPing, stopPingInterval, stopCloseTimeout, checkServer, userIsInactive],
  );

  // Start the wait process to close the socket if the user is inactive
  const startCloseSocketTimeout = useCallback(() => {
    sendPing(); // We want to keep track of when user becomes inactive

    // Don't create another timer if we already have one
    if (closeTimeout.current) {
      return;
    }

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      const terminateSocketTimeoutMS = isProjectorView()
        ? PROJECTOR_TERMINATE_TIMEOUT_MS
        : TERMINATE_TIMEOUT_MS;

      closeTimeout.current = setTimeout(() => {
        if (isInVirtualClassRef.current) {
          logInfoWithDebugFlag("debug_pubsub", "Not closing socket due to virtual class presence");
          // Don't close the socket if we're in a virtual class
          return;
        }
        if (socket.readyState === WebSocket.OPEN) {
          socket.close(NORMAL_SOCKET_CLOSE_CODE, "User inactive");
          dispatch(viewStateActions.setUserIsInactive(true));

          logInfoWithDebugFlag("debug_pubsub", "Closing socket due to user inactivity", {
            courseId: selectCurrentCourseId(store.getState()),
            websocketCourseId: selectWebsocketCourseId(store.getState()),
            windowId: getWindowId(),
            pageId: pageIdRef.current,
          });
        }

        // Closed socket so we can stop pinging
        stopPingInterval();
      }, terminateSocketTimeoutMS);
    }
  }, [socket, stopPingInterval, sendPing, dispatch]);

  // If the page changes send an immediate ping
  // or if the user's following status changes
  // or we need an auth update
  useEffect(() => {
    if (
      pageIdRef.current !== pageId ||
      isFollowingRef.current !== isFollowing ||
      shouldRequestAuth
    ) {
      pageIdRef.current = pageId;
      isFollowingRef.current = isFollowing;
      sendPing();
    }
  }, [pageId, isFollowing, shouldRequestAuth, sendPing]);

  const pubsubStatsCollector = useContext(PubsubStatsCollectorContext);

  // socket listeners
  useEffect(() => {
    const handleWebsocketMessage = (event: { data: string }) => {
      try {
        const message = JSON.parse(event.data) as PongMessageType;

        if (message.a === "pong") {
          serverLastResponseRef.current = Date.now();

          if (monitoring) {
            return;
          }

          if (message.startTime) {
            pubsubStatsCollector?.addPingRecord(Date.now() - message.startTime);
          }

          // Handle version mismatch detection via pong messages
          const state = store.getState();
          const currentStoredVersion = selectStoredCollaborationServerVersion(state);
          const shouldShowModal = message.forceReloadModal === true;

          if (currentStoredVersion === null) {
            // First time receiving version - store it, no comparison
            dispatch(viewStateActions.setStoredCollaborationServerVersion(message.serverVersion));
          } else if (currentStoredVersion !== message.serverVersion) {
            // Stored version mismatches server version - version change detected
            // Don't update stored version - keep it as original so modal/banner stays visible
            if (shouldShowModal) {
              // Show force reload modal
              dispatch(viewStateActions.setShowForceReloadModal(true));
              dispatch(viewStateActions.setShowVersionMismatchWarning(false));
            } else {
              // Show banner instead
              dispatch(viewStateActions.setShowForceReloadModal(false));
              dispatch(viewStateActions.setShowVersionMismatchWarning(true));
            }
          } else {
            // Stored version matches server version - clear warnings
            dispatch(viewStateActions.setShowVersionMismatchWarning(false));
            dispatch(viewStateActions.setShowForceReloadModal(false));
          }
        }
      } catch {
        // Other listeners will handle error checking
      }
    };

    const handleOpen = () => {
      if (!document.hasFocus()) startCloseSocketTimeout();
      startPingInterval();
    };

    socket.addEventListener("open", handleOpen);
    socket.addEventListener("message", handleWebsocketMessage);
    socket.addEventListener("close", stopPingInterval);

    return () => {
      // remove all intervals and listeners
      stopPingInterval();
      stopCloseTimeout();

      socket.removeEventListener("open", handleOpen);
      socket.removeEventListener("message", handleWebsocketMessage);
      socket.removeEventListener("close", stopPingInterval);
    };
  }, [
    socket,
    stopPingInterval,
    startPingInterval,
    stopCloseTimeout,
    dispatch,
    pubsubStatsCollector,
    monitoring,
    startCloseSocketTimeout,
  ]);

  // window listeners
  useEffect(() => {
    const handleFocus = () => {
      dispatch(viewStateActions.setUserIsInactive(false));

      startPingInterval(false);
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", startCloseSocketTimeout);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", startCloseSocketTimeout);
    };
  }, [startPingInterval, startCloseSocketTimeout, dispatch]);

  useEffect(() => {
    isInVirtualClassRef.current = isInVirtualClass;
    if (!isInVirtualClass && !document.hasFocus()) {
      startCloseSocketTimeout();
    }
    // We only want to run this when isInVirtualClass changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInVirtualClass]);

  return null;
};
